import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EnvState } from 'app/modules/shared/helpers/env-state';
import { AuthRepository, Office365Settings } from 'app/state/auth.repository';
import { AuthService } from 'app/state/auth.service';
import { HttpClient } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { NotificationService } from 'app/api/services/notificationService';
import { TenantSyncService } from 'app/api/services/tenant-sync.service';
import { TrackRender } from 'app/shared/front-perfomance';

@TrackRender()
@Component({
    selector: 'app-signin',
    templateUrl: './signin.component.html',
    styleUrls: ['./signin.component.scss'],
    standalone: false
})
export class SigninComponent implements OnInit {
  title: any;
  error: string | null = null;
  testsLocation = 'localhost:9876';
  path = '';
  settings$: Observable<Office365Settings> | null = null;
  enable365 = false;
  // Toggled by the eye icon next to the password field. When true, the
  // <input> type flips from "password" to "text" so the user can verify
  // what they typed.
  passwordVisible = false;

  /** Two languages are configured app-wide (see transloco-root.module.ts). */
  private readonly supportedLangs = ['en', 'da'];
  loginForm = this.formBuilder.group({
    email: ['', [Validators.email, Validators.required]],
    password: ['', Validators.required],
    rememberMe: false,
  });

  constructor(
    private auth: AuthService,
    public repo: AuthRepository,
    private route: ActivatedRoute,
    public router: Router,
    private formBuilder: UntypedFormBuilder,
    public env: EnvState,
    private http: HttpClient,
    public service: TranslocoService,
    public notificationsservice: NotificationService,
    private tenantSync: TenantSyncService,
  ) {
    repo.returnUrl$ = this.route.queryParamMap.pipe(
      map((params) => params.get('returnUrl') || '/'),
    );
  }
  private baseUrlPattern = /^https:\/\/nybolig\.igntr\.me(\/.*)?$/;
  // private baseUrlPattern = /^https:\/\/localhost:4200(\/.*)?$/; // For testing purposes
  ngOnInit(): void {
    this.enable365 = this.baseUrlPattern.test(document.location.href);
  }

  loadSettings() {
    this.path = '';
    this.http.get<Office365Settings>('/api/auth/office').subscribe({
      next: (x) => {
        if (x) {
          // console.log(
          //   `https://login.microsoftonline.com/${x.tenantId}/oauth2/v2.0/authorize?client_id=${x.clientId}&response_type=code&redirect_uri=` +
          //     x.url +
          //     `&response_mode=query&scope=${x.scope}&state=12345`,
          // );
          this.path =
            `https://login.microsoftonline.com/${x.tenantId}/oauth2/v2.0/authorize?client_id=${x.clientId}&response_type=code&redirect_uri=` +
            x.url +
            `&response_mode=query&scope=${x.scope}&state=12345`;
        }
      },
      complete: () => {
        if (this.path) {
          document.location.href = this.path;
        }
      },
    });
  }

  private showTooltip() {
    var tooltiptriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );
    tooltiptriggerList.map(function (e: any) {
      return new bootstrap.Tooltip(e);
    });
  }

  private renderError(err: any) {
    this.title =
      err?.error ?? 'An unexpected error occurred. Please try again.';
    setTimeout(() => this.showTooltip());
  }

  /** Label shown in the top-right language switcher (e.g. "DA", "EN"). */
  get currentLang(): string {
    return (this.service.getActiveLang() || 'en').toUpperCase();
  }

  /**
   * Cycle through supported UI languages. Two languages today (en/da) so
   * this effectively toggles between them; written as a rotation so adding
   * a third language doesn't require touching this method.
   */
  toggleLanguage(): void {
    const current = this.service.getActiveLang();
    const idx = this.supportedLangs.indexOf(current);
    const next = this.supportedLangs[(idx + 1) % this.supportedLangs.length];
    this.service.setActiveLang(next);
    localStorage.setItem('curUserLang', next);
  }

  submit() {
    this.auth
      .login(
        this.loginForm.value.email,
        this.loginForm.value.password,
        this.loginForm.value.rememberMe,
      )
      .subscribe({
        next: (responce) => {
          if (responce.user) {
            this.repo.setUser(responce.user);
            // Only seed Transloco from the server profile if the user has
            // not chosen a UI language via the switcher above. Otherwise
            // their selection (already persisted in localStorage by
            // toggleLanguage) would be silently overridden on every login.
            if (!localStorage.getItem('curUserLang')) {
              const serverLang = responce?.user?.language ?? 'en';
              this.service.setActiveLang(serverLang);
              localStorage.setItem('curUserLang', serverLang);
            }
          }
          this.repo.setToken(responce.token);

          setTimeout(() => {
            this.tenantSync.connect().then(() => {});
          }, 3000);

          this.router.navigate(['active-tenant-page']);
        },
        error: (error) => {
          this.renderError(error);
        },
      });

    const FCMToken = localStorage.getItem('firebaseToken');
    this.notificationsservice.sendTokenToServer(FCMToken!).subscribe();
  }
}
